import React from 'react';
import Input from './Input';
import FormField from './FormField';
import { validateDateRange } from '../../shared/dateRange';

// Reusable start/end date pair with the "start can't be after end" rule baked in.
// Use this anywhere a user picks a date range (trip form, journey dates, …) so the
// validation + inline alert never have to be re-implemented. The rule itself lives
// in shared/dateRange.ts (mirrored on the backend); this component owns the inputs
// and the elegant inline message shown right under the fields.
//
// Validity is derived from the same shared validateDateRange(), so a parent that
// needs to gate a Save/Submit can call it on the same values (single source of
// truth) — see JourneyDates / TripFormPage.

export interface DateRangeFieldsProps {
  /** yyyy-mm-dd or '' */
  start: string;
  end: string;
  onChange: (next: { start: string; end: string }) => void;
  /** 'stack' = two columns with labels above (forms); 'inline' = compact row (pills). */
  layout?: 'stack' | 'inline';
  startLabel?: string;
  endLabel?: string;
  disabled?: boolean;
  /** Label color override for the inline layout (e.g. on a green pill). */
  labelColor?: string;
}

// Single click anywhere in a date field pops the native calendar. Stored per
// element so it stays stateless. Shared by both layouts.
const openPickerOnEntry = {
  onMouseDown: (e: React.MouseEvent<HTMLInputElement>) => {
    (e.currentTarget as any)._wasFocused = document.activeElement === e.currentTarget;
  },
  onClick: (e: React.MouseEvent<HTMLInputElement>) => {
    if (!(e.currentTarget as any)._wasFocused) {
      try { (e.currentTarget as any).showPicker?.(); } catch { /* unsupported browser */ }
    }
  },
};

const WarnIcon: React.FC = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
    <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InlineAlert: React.FC<{ message: string }> = ({ message }) => (
  <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#b4452f', fontSize: 12, fontWeight: 600, marginTop: 6 }}>
    <WarnIcon />
    <span>{message}</span>
  </div>
);

const DateRangeFields: React.FC<DateRangeFieldsProps> = ({
  start,
  end,
  onChange,
  layout = 'stack',
  startLabel = 'Start date',
  endLabel = 'End date',
  disabled,
  labelColor,
}) => {
  const error = validateDateRange(start, end);
  const setStart = (v: string) => onChange({ start: v, end });
  const setEnd = (v: string) => onChange({ start, end: v });

  // Cross-wire the native pickers so the calendar itself nudges valid picks; the
  // inline alert still covers typed/out-of-order input on top of that.
  const startInput = (
    <Input
      type="date"
      value={start}
      max={end || undefined}
      disabled={disabled}
      error={!!error}
      aria-invalid={!!error || undefined}
      {...openPickerOnEntry}
      onChange={(e) => setStart(e.target.value)}
    />
  );
  const endInput = (
    <Input
      type="date"
      value={end}
      min={start || undefined}
      disabled={disabled}
      error={!!error}
      aria-invalid={!!error || undefined}
      {...openPickerOnEntry}
      onChange={(e) => setEnd(e.target.value)}
    />
  );

  if (layout === 'inline') {
    const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, fontWeight: 600, color: labelColor || '#5a8a78' };
    return (
      <div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={lbl}>{startLabel}<span style={{ width: 138 }}>{startInput}</span></label>
          <label style={lbl}>{endLabel}<span style={{ width: 138 }}>{endInput}</span></label>
        </div>
        {error && <InlineAlert message={error} />}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label={startLabel} style={{ marginBottom: 0 }}>{startInput}</FormField>
        <FormField label={endLabel} style={{ marginBottom: 0 }}>{endInput}</FormField>
      </div>
      {error && <InlineAlert message={error} />}
    </div>
  );
};

export default DateRangeFields;
